import JSONfn from "json-fn"
import JSZip from "jszip"
import type { AnimationI, AnySceneObjectT, CanvasI, SimpleStyleT, TimelineI } from "tweenly"
import { getFilePath, getFontPath, FileProcessor as LibFileProcessor } from "tweenly"
import { AssetT } from "../store/slices/assets.slice"
import { GraphicT } from "../store/slices/graphic.slice"
import { FileResponse, get, getFile } from "./ajax.helpers"
import { S3_URL } from "./file.helpers"
import {
    fetchGoogleFontsCss,
    rewriteGoogleFontsCssToLocal,
    SYSTEM_FONTS,
} from "./googleFonts.helpers"

export class FileProcessor {
    private allObjects: AnySceneObjectT[]
    private assets: AssetT[]
    private timeline: TimelineI
    private canvas: CanvasI
    private graphic: GraphicT
    private processor: LibFileProcessor

    constructor(
        allObjects: AnySceneObjectT[],
        animations: AnimationI[],
        assets: AssetT[],
        timeline: TimelineI,
        canvas: CanvasI,
        graphic: GraphicT,
        freeVersionHtml: boolean
    ) {
        this.allObjects = allObjects
        this.assets = assets
        this.timeline = timeline
        this.canvas = canvas
        this.graphic = graphic
        this.processor = new LibFileProcessor(
            allObjects,
            animations,
            assets,
            timeline,
            canvas,
            graphic,
            freeVersionHtml,
            S3_URL
        )
    }

    // Delegate HTML generation to the shared lib exporter.
    processFile = (
        basePath: string | undefined,
        files: FileResponse[] | undefined,
        freeVersionHtml: boolean,
        localGoogleFontsCss?: string
    ): string => this.processor.processFile(basePath, files, freeVersionHtml, localGoogleFontsCss)

    // Generate a scene.json that can be re-imported back into Tweenly.
    // Uses JSONfn.stringify so that render functions in object styles are preserved.
    generateSceneJson = (): string => {
        return JSONfn.stringify({
            _tweenly_export_version: "1.0",
            canvas: this.canvas,
            objects: this.allObjects,
            timeline: this.timeline,
        })
    }

    processZip = async (loadingCallback: (value: number) => void) => {
        const zip = new JSZip()
        let basePath = window.location.origin
        const libResponse = await fetch(`${basePath}/scripts/tweenly.umd.cjs`)
        if (libResponse.ok) {
            zip.file("scripts/tweenly.umd.cjs", await libResponse.blob())
        }
        basePath = "."

        let files: FileResponse[] = []

        // Collect objects that need image files from S3:
        // - native image/sequence objects
        // - any object with a backgroundImage fill referencing S3 (e.g. Pencil imports)
        const images = this.allObjects.filter(
            (obj) =>
                obj.type === "image" ||
                obj.type === "sequence" ||
                obj.fill.some(
                    (f) =>
                        f.property === "backgroundImage" &&
                        typeof f.value === "string" &&
                        f.value.includes(S3_URL)
                )
        )

        if (images.length) {
            files = await get("files", {
                key: `/(images|sequences)/(${images.map((image) => image.id).join("|")})`,
            })

            for (let i = 0; i < files.length; i++) {
                const file = files[i]
                const path = getFilePath(file)
                if (path) {
                    try {
                        const data = await getFile(file._id)
                        zip.file(path, data)
                    } catch (error) {
                        console.error(`File ${file._id} not found.`)
                    }
                }
                loadingCallback(Number(((i / files.length) * 100) / 2))
            }
        }

        for (const asset of this.assets.filter((asset) => asset.type === "font")) {
            const path = getFontPath(asset)
            try {
                const data = await getFile(asset.slug)
                zip.file(path, data)
            } catch (error) {
                console.error(`File ${asset.slug} not found.`)
            }
        }

        // Package Google Fonts locally: download only actually-used weight variants
        let localGoogleFontsCss: string | undefined
        const customFontNames = new Set(
            this.assets.filter((a) => a.type === "font").map((a) => a.name)
        )
        const googleFontWeights = new Map<string, Set<number>>()
        this.allObjects.forEach((obj) => {
            const ff = obj.styles.find((s: SimpleStyleT) => s.property === "fontFamily")
            if (ff) {
                const name = String(ff.value)
                if (!SYSTEM_FONTS.has(name) && !customFontNames.has(name)) {
                    if (!googleFontWeights.has(name)) {
                        googleFontWeights.set(name, new Set())
                    }
                    const fw = obj.styles.find((s: SimpleStyleT) => s.property === "fontWeight")
                    const weight = fw ? Number(fw.value) || 400 : 400
                    googleFontWeights.get(name)!.add(weight)
                }
            }
        })
        const uniqueGoogleFontNames = Array.from(googleFontWeights.keys())

        if (uniqueGoogleFontNames.length > 0) {
            try {
                const { css, fontFiles } = await fetchGoogleFontsCss(
                    uniqueGoogleFontNames,
                    googleFontWeights
                )
                if (css && fontFiles.length > 0) {
                    // Download each font file and add to ZIP
                    for (const fontFile of fontFiles) {
                        try {
                            const fontResponse = await fetch(fontFile.url)
                            if (fontResponse.ok) {
                                const fontBlob = await fontResponse.blob()
                                zip.file(fontFile.localPath, fontBlob)
                            }
                        } catch (err) {
                            console.error(`Failed to download font file: ${fontFile.url}`, err)
                        }
                    }
                    // Rewrite CSS to use local paths
                    localGoogleFontsCss = rewriteGoogleFontsCssToLocal(css, fontFiles)
                    console.log(
                        `🔤 Packaged ${
                            fontFiles.length
                        } Google Font files for: ${uniqueGoogleFontNames.join(", ")}`
                    )
                }
            } catch (err) {
                console.error("Failed to fetch Google Fonts CSS:", err)
            }
        }

        let content = this.processFile(basePath, files, false, localGoogleFontsCss)
        zip.file("index.html", content)

        // Add .tw file for round-trip import back into Tweenly (JSON content, named after the graphic)
        const twFileName = this.graphic.name.replace(/[/\\?%*:|"<>]/g, "-") + ".tw"
        zip.file(twFileName, this.generateSceneJson())

        //updateCallback(metadata) is called for set perceptual Loading when generate ZIP file
        return await zip.generateAsync({ type: "blob" }, function updateCallback(metadata) {
            loadingCallback(Number(100 / 2 + metadata.percent / 2))
        })
    }
}
